import { useCallback, useEffect, useRef } from 'react';

import type { Action } from 'pkg/api/models/auto/action';
import * as sdk from 'pkg/core/sdk';
import useMixedState from 'pkg/hooks/useMixedState';
import * as logger from 'pkg/logger';

import { useApiConfig } from 'components/application/apiconfig';

interface RequestHeaders {
	[key: string]: string;
}

interface EndpointOptions {
	queryParams?: URLSearchParams;
	/**
	 * use a POST request instead of GET
	 */
	postRequest?: boolean;
	body?: {
		[key: string]: unknown;
	};
	delayFetch?: boolean;
	requestHeaders?: RequestHeaders;
	defaultResponse?: any;
	/**
	 * Which allowed actions should be loaded from the backend, the response will include
	 * a subset of this list with the allowed actions.
	 */
	actions?: Action[];
	/**
	 * Keep the record last read when a later read fails, for a page that
	 * polls or refreshes and must not go blank: `hasError` and `status` still
	 * say the read failed, and `afterFetch` is not called for it.
	 */
	keepRecordOnError?: boolean;
}

export interface EndpointResponse<T> {
	record: T;
	isLoading: boolean;
	isInitialLoad: boolean;
	hasError?: boolean;
	response?: Response;
	status?: number;
	refresh: () => Promise<void>;
	replaceRecord: (record: T) => void;
	replaceInRecord: (record: T, key: keyof T, newRecord: any, id?: any) => T;
	removeInRecord: (record: T, key: keyof T, removeRecord: any) => void;
	addInRecord: (record: T, key: keyof T, newRecords: any) => void;
}

/**
 *
 * @param endpoint request this endpoint, complete with URL params
 * @returns
 */
export function useEndpoint<T>(
	endpoint: string,
	options: EndpointOptions = {},
	afterFetch?: (record: T, response: Response) => void | T
): EndpointResponse<T> {
	const apiConfig = useApiConfig();
	const {
		queryParams = options.queryParams || new URLSearchParams(),
		requestHeaders,
		delayFetch,
		defaultResponse = {},
		body = {},
		postRequest,
		actions,
		keepRecordOnError,
	} = options;

	// What a failed read leaves in place of the record.
	const failedRecord = (): Partial<EndpointResponse<T>> =>
		keepRecordOnError ? {} : { record: defaultResponse };

	const fetchData = async () => {
		if (!endpoint) {
			return;
		}

		if (!responseData.isLoading) {
			setState({ isLoading: true });
		}

		const params = new URLSearchParams(queryParams);
		if (actions?.length) {
			params.set('allowed_actions', actions.join(','));
		}

		const reqOptions = Object.fromEntries(params.entries());

		let r: Response;
		let d: any;

		try {
			r = postRequest
				? await sdk.post(endpoint, reqOptions, body, requestHeaders || {})
				: await sdk.get(endpoint, reqOptions, {}, requestHeaders || {});
		} catch (e) {
			logger.error(
				new Error('failed to make request', {
					cause: e,
				}),
				{
					method: postRequest ? 'POST' : 'GET',
					url: endpoint,
				}
			);
			setState({
				...failedRecord(),
				response: r,
				hasError: true,
				isLoading: false,
				status: r?.status,
			});
			return;
		}

		if ([426, 503].includes(r?.status)) {
			apiConfig.refreshConfig();
			return;
		}

		if (r?.status >= 500 && r?.status < 600) {
			logger.error(new Error(`server error ${r.statusText}`), {
				statusText: r.statusText,
				status: r.status,
				url: endpoint,
			});
			setState({
				hasError: true,
				response: r,
				isLoading: false,
				status: r?.status,
			});
			return;
		}

		try {
			d = await r.json();
		} catch (e) {
			logger.error(new Error('failed to parse json', { cause: e }), {
				url: endpoint,
			});
			setState({
				...failedRecord(),
				response: r,
				hasError: true,
				isLoading: false,
				status: r?.status,
			});
			return;
		}

		const newState = {
			...responseData,
			response: r,
			isLoading: false,
			isInitialLoad: false,
			status: r?.status,
		};

		if (r?.ok) {
			newState.record = d as T;
			newState.hasError = false;
		} else if (keepRecordOnError) {
			// Only what says it failed: the record on screen, which may have
			// been replaced while this read was out, stays.
			setState({
				response: r,
				isLoading: false,
				isInitialLoad: false,
				status: r?.status,
				hasError: true,
			});
			return;
		} else {
			newState.record = defaultResponse;
			newState.hasError = true;
		}

		if (afterFetch) {
			let modifiedResponse = afterFetch(newState.record, r);
			if (modifiedResponse) {
				newState.record = modifiedResponse;
			}
		}
		setState(newState);
	};

	const replaceRecord = (record: T) => {
		setState({
			record,
		});
	};

	// replaces a record at key with newRecord. If newRecord is a function and an id is also passed,
	// it is called with the current record and is expected to return an updated version of the record.
	const replaceInRecord = (
		record: T,
		key: keyof T,
		newRecord: any,
		id?: any
	) => {
		const innerRecords = (record[key] || []) as any[any];

		if (typeof newRecord === 'function' && typeof id !== 'undefined') {
			const index = innerRecords.findIndex((inner: any) => inner.id === id);
			innerRecords[index] = newRecord(innerRecords[index]);
		} else {
			const index = innerRecords.findIndex(
				(inner: any) => inner.id === newRecord.id
			);
			innerRecords[index] = newRecord;
		}
		record[key] = innerRecords;

		setState({
			record,
		});

		return record;
	};

	const removeInRecord = (record: T, key: keyof T, removeRecord: any) => {
		const innerRecords = (record[key] || []) as any[any];
		const index = innerRecords.findIndex(
			(inner: any) => inner.id === removeRecord.id
		);

		const newInnerRecords = innerRecords.filter(
			(r: any, i: number) => i !== index
		);

		record[key] = newInnerRecords;

		setState({
			record,
		});

		return;
	};

	const addInRecord = (record: T, key: keyof T, newRecords: any) => {
		const innerRecords = (record[key] || []) as any[any];
		const newInnerRecords: any = [...innerRecords, ...newRecords];

		record[key] = newInnerRecords;

		setState({
			record,
		});

		return;
	};

	let isLoading = true;

	if (!endpoint || delayFetch) {
		isLoading = false;
	}

	// Without this ref, refresh would call the first render's fetchData (which
	// captured the first render's queryParams). Manual refresh after filters or
	// sort change would then refetch with stale params.
	const fetchDataRef = useRef(fetchData);
	fetchDataRef.current = fetchData;

	const refresh = useCallback(() => fetchDataRef.current(), []);

	const [responseData, setState] = useMixedState<EndpointResponse<T>>({
		isLoading,
		isInitialLoad: true,
		refresh,
		record: defaultResponse as T,
		replaceRecord,
		replaceInRecord,
		removeInRecord,
		addInRecord,
	});

	useEffect(() => {
		if (!delayFetch && !!endpoint) {
			fetchData();
		}
	}, [
		endpoint,
		delayFetch,
		queryParams?.toString(),
		JSON.stringify(body),
		actions?.join(','),
	]);

	return responseData;
}
